import { darken, transparentize } from 'polished';
import React from 'react';
import styled from 'styled-components';
import { useObserver } from '../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { Icon } from '../../concepts/resources/Icon';
import { Colors, toRem } from '../../ui';
import { Position } from '../../ui/Position';
import { useResolve } from '../Container';
import { usePermissionCheck } from '../common/PermissionElements';
import { WorkspaceModel } from '../dashboard/Models';
import { boardBuild, boardCreateAndOpen } from './BoardCommands';
import { folderCreateInCurrentWorkspace } from './FolderCommands';
import { QuickMenuStrip } from './QuickMenuStrip';

const IconFrame = styled.div`
    /* padding: .5rem;
    background: ${transparentize(.2, Colors.positive)};
    border-radius: .375rem; */
`;

const Label = styled.div`
    display: flex;
    align-items: center;
    margin-top: 0.375rem;
    margin-bottom: 1rem;
    font-size: 1rem;
    font-weight: 450;

    & svg, img {
        margin-right: .25em;
    }
`;

const ItemFrame = styled.div<{ color?: string, selected?: boolean }>`
    position: relative;
    padding-top: 70%;

    height: 100%;
    min-width: 0;
    grid-column-end: span 1;
    grid-row-end: span 1;

    box-shadow: ${p => p.selected ? `0 0 0 .125rem ${Colors.primary}` : '0 4px 8px 0 rgb(0 0 0 / 4%)'};
    background: ${p => p.color ?? 'white'};
    color: ${p => !!p.color ? 'white' : p.selected ? Colors.primary : Colors.text};
    /* border: solid .0625rem ${Colors.border}; */
    border-radius: .25rem;
    cursor: pointer;
    user-select: none;

    &:hover { background: ${p => p.color ? darken(0.05, p.color) : 'white'} }
    &:focus { background: ${p => p.color ? darken(0.05, p.color) : 'white'} }
    &:active { background: ${p => p.color ? darken(0.10, p.color) : 'white'} }
`;

const iconSizes = {
    reg: 3,
    min: 0,
};

const labelFontSizes = {
    reg: 1,
    min: .8125,
};

const ItemStrip = styled.div<{ bp: string }>`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 10rem));
    grid-gap: 1rem;
    max-width: 90rem;

    ${IconFrame} {
        svg, img {
            width: ${p => toRem(iconSizes[p.bp])}; 
            height: ${p => toRem(iconSizes[p.bp])}; 
        }
    }

    ${Label} {
        position: relative;
        font-size: ${p => toRem(labelFontSizes[p.bp])}; 

        svg, img {
            width: ${p => toRem(labelFontSizes[p.bp])}; 
            height: ${p => toRem(labelFontSizes[p.bp])}; 
        }
        
        ${p => p.bp === 'min' ? 'margin: 0;' : ''}
    }
`;

const ItemStripFrame = styled.div`
    padding: 1.5rem 2rem;
    background: ${Colors.hover};
`;

const Item = (props: { label: string, icon: string, bp: string }) =>
    <Position at={props.bp === 'min' ? 'center' : 'bottom'} flow="column">
        {props.bp === 'reg' && <IconFrame>
            <Icon src={props.icon} />
        </IconFrame>}
        <Label>
            <Icon src="deqo://icons/deqo/add" />
            <span>{props.label}</span>
        </Label>
    </Position>;

export interface WorkspaceStripMenuProps {
    folders?: boolean;
    boards?: boolean;
    templates?: boolean;
}

export const WorkspaceStripMenu = ({ folders, boards, templates }: WorkspaceStripMenuProps) => {

    const dispatch = useResolve(CommandDispatcher);

    const { joined } = useObserver(useResolve(WorkspaceModel));
    const canEdit = usePermissionCheck('da:write', 'wo:write') && joined;

    const onNewFolder = () => dispatch(folderCreateInCurrentWorkspace);
    const onBlankBoard = async () => await dispatch(boardCreateAndOpen, ['BLANK']);
    const onUseTemplate = async () => await dispatch(boardBuild);

    return <QuickMenuStrip>
        {folders !== false &&
            <QuickMenuStrip.Button
                label="New Folder"
                icon="deqo://icons/dash/add-folder"
                color={Colors.positive}
                disabled={!canEdit}
                onClick={onNewFolder}
            />
        }
        {boards !== false &&
            <QuickMenuStrip.Button
                label="Blank Board"
                icon="deqo://icons/dash/add-board"
                color={Colors.primaryLight}
                disabled={!canEdit}
                onClick={onBlankBoard}
            />
        }
        {templates !== false &&
            <QuickMenuStrip.Button
                label="Use Template"
                icon="deqo://icons/dash/add-template"
                color={Colors.primaryLight}
                disabled={!canEdit}
                onClick={onUseTemplate}
            />
        }
    </QuickMenuStrip>;
};


